import React, { useState } from "react";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Card } from "@amzn/stencil-react-components/card";
import { IconCheckCircleFill } from "@amzn/stencil-react-components/icons";
import { Col } from "@amzn/stencil-react-components/layout";
import { Text } from "@amzn/stencil-react-components/text";
import Dropzone, { ILayoutProps } from "react-dropzone-uploader";
import isEmpty from "lodash/isEmpty";
import { H4 } from "../typography";
import {
  CandidateBgcAttachmentSubType,
  FileChangeEvent,
  getSelectedFileType,
  removeFile,
  setFile,
  toggleSubmitButton,
} from "../../reduxStore/slices/docUploadSlice";
import { useAppDispatch, useAppSelector } from "../../reduxStore/reduxHooks";
import { CandidateBgcAttachmentMetadata, Props, ReactComponent, UploadedStatus } from "./types";
import {
  ALLOWED_FILE_TYPES,
  documentBackTitleMessage,
  documentFrontTitleMessage,
  documentUploadErrorText,
  dropZoneAttributes,
  inputAttributes,
  previewAttributes,
} from "./constants";
import { useFileUploadError } from "./hooks/useFileUploadError";
import { Status, StatusIndicator } from "@amzn/stencil-react-components/status-indicator";
import { getDocSizeWithMetric, getSha256FromBase64 } from "./helpers";
import { dispatchOnDev } from "../../utility/dev-env-helpers";

export const toDataURL = (
  src: string,
  fileType: string,
  height: number,
  width: number,
  callback: (arg0: string) => void
) => {
  const image = new Image();
  image.onload = function () {
    const canvas = document.createElement("canvas");
    const context: any = canvas.getContext("2d");
    canvas.height = height;
    canvas.width = width;
    context.drawImage(this, 0, 0);
    const dataURL = canvas.toDataURL(fileType);
    callback(dataURL);
  };
  image.src = src;
};

export const processImage = (files: any, side: string, onFileChanged: any) => {
  setTimeout(() => {
    toDataURL(
      files.meta?.previewUrl,
      files.meta?.type,
      files.meta?.height,
      files.meta?.width,
      (imageBase64: string) => {
        onFileChanged(
          {
            imageBase64: imageBase64,
            name: files.meta.name,
            type: files.meta.type,
            side: side,
            size: String(files.meta.size),
          },
          FileChangeEvent.ADDED
        );
      }
    );
  }, 100);
};

export const processPDF = (files: any, side: string, onFileChanged: any) => {
  const pdf = files.file;
  const reader = new FileReader();
  reader.readAsDataURL(pdf);
  reader.onload = function (e) {
    const image = e.target?.result as string;
    if (image) {
      onFileChanged(
        {
          imageBase64: image,
          name: files.meta.name,
          type: files.meta.type,
          side: side,
        },
        FileChangeEvent.ADDED
      );
    }
  };
};

export const handleUpdate = (files: any, side: string, onError: any, onFileChanged: any) => {
  if (files.meta.size / (1024 * 1024) < 7 && ALLOWED_FILE_TYPES.includes(files.meta.type)) {
    onError(null);
    if (files.meta.type === "application/pdf") {
      processPDF(files, side, onFileChanged);
    } else {
      processImage(files, side, onFileChanged);
    }
  } else {
    onError(documentUploadErrorText);
  }
};

export const handleFileRemoval = (files: any, side: string, onError: any, onFileChanged: any) => {
  onError(null);
  onFileChanged(
    {
      imageBase64: "",
      name: files.meta.name,
      type: files.meta.type,
      side: side,
    },
    FileChangeEvent.REMOVED
  );
};

export const getLayout = (
  defaultValue: CandidateBgcAttachmentMetadata | undefined,
  uploadedStatus: UploadedStatus,
  setUploadedStatus: React.Dispatch<React.SetStateAction<UploadedStatus>>
): ReactComponent<ILayoutProps> => {
  const { uploaded, replaceUploaded } = uploadedStatus;

  if (!uploaded && !replaceUploaded && !isEmpty(defaultValue) && !defaultValue?.imageBase64) {
    setUploadedStatus({ uploaded: true, replaceUploaded: false });
  }

  const Layout = ({ input, previews, dropzoneProps, files, extra: { maxFiles } }: ILayoutProps) => {
    const replaceButtonText = "Replace";
    const uploadCompleteText = "Upload complete";

    const removeFile = () => {
      if (!uploaded) {
        files[0].remove();
      } else {
        setUploadedStatus({ uploaded: false, replaceUploaded: true });
      }
    };

    return (
      <div
        style={{
          height: uploaded || files.length ? "100%" : "100%",
          width: "100%",
        }}
      >
        {uploaded || files.length ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Col
              style={{
                height: "100%",
                width: "100%",
                overflow: "hidden",
                justifyContent: "center",
              }}
            >
              {uploaded || files[0].meta.type === "application/pdf" ? (
                <Col style={{ alignItems: "center" }}>
                  <IconCheckCircleFill aria-hidden color="#23756B" style={{ paddingTop: "30px" }} />
                  <Text fontWeight="bold">{uploadCompleteText}</Text>
                  {uploaded && (
                    <Text fontSize="T100" title={defaultValue?.fileName} className="filename-ellipsis">
                      {defaultValue?.fileName}
                    </Text>
                  )}
                </Col>
              ) : (
                previews
              )}
            </Col>
            <Button
              variant={ButtonVariant.Tertiary}
              onClick={removeFile}
              style={{ alignSelf: "bottom", height: "10px" }}
            >
              {replaceButtonText}
            </Button>
          </div>
        ) : (
          <div {...dropzoneProps}>{files.length < maxFiles && input}</div>
        )}
      </div>
    );
  };

  return Layout;
};

export const SelectFileToUploadCard = ({ side, optional, defaultValue, onError, onFileChanged }: Props) => {
  if (optional && side === CandidateBgcAttachmentSubType.FRONT) {
    dispatchOnDev(() => {
      console.error("Document front cannot be optional, overriding to required");
    });
    optional = false;
  }

  const handleStatusChange = (files: any, side: string, status: any) => {
    if (status === "done") {
      handleUpdate(files, side, onError, onFileChanged);
    } else if (status === "removed") {
      handleFileRemoval(files, side, onError, onFileChanged);
    } else if (status === "rejected_file_type") {
      onError(documentUploadErrorText);
    }
  };

  const [uploadedStatus, setUploadedStatus] = useState<UploadedStatus>({
    uploaded: false,
    replaceUploaded: false,
  });

  return (
    <Card padding={"8px"} margin={"16px 40px"} height={"200px"} isElevated={true}>
      <Dropzone
        data-test-id="some"
        accept="image/*,application/pdf"
        onChangeStatus={(files, status) => handleStatusChange(files, side, status)}
        multiple={false}
        maxFiles={1}
        inputContent={
          side === CandidateBgcAttachmentSubType.FRONT
            ? documentFrontTitleMessage + "*"
            : documentBackTitleMessage + "*"
        }
        styles={{
          inputLabel: inputAttributes,
          dropzone: dropZoneAttributes,
          preview: previewAttributes,
        }}
        LayoutComponent={getLayout(defaultValue, uploadedStatus, setUploadedStatus)}
      />
    </Card>
  );
};

export const SelectFileToUpload = () => {
  const { requiredPages } = useAppSelector((state) => state.docUpload);
  const dispatch = useAppDispatch();
  const selectedFile = useAppSelector(getSelectedFileType);
  const { errors, onError } = useFileUploadError(requiredPages);

  const validateForm = () =>
    dispatch((dispatch, getState) => {
      if (getState().docUpload.requiredPages.every((el) => getState().docUpload.files[el] !== undefined)) {
        dispatch(toggleSubmitButton({ isEnabled: true }));
      } else {
        dispatch(toggleSubmitButton({ isEnabled: false }));
      }
    });

  const onFileChanged: Props["onFileChanged"] = async (file, changeEvent) => {
    const sha256 = await getSha256FromBase64(file);
    if (changeEvent === FileChangeEvent.ADDED) {
      dispatch(
        setFile({
          ...file,
          size: getDocSizeWithMetric(file.size),
          id: String(Date.now()),
          documentTypeId: selectedFile ? selectedFile?.documentType : "",
          sha256,
        })
      );
    }
    if (changeEvent === FileChangeEvent.REMOVED) {
      dispatch(removeFile(file));
    }

    validateForm();
  };

  return (
    <>
      <H4 margin="16px 0" textAlign="center">
        {selectedFile && selectedFile?.displayValue}
      </H4>
      {requiredPages.map((card) => {
        return (
          <>
            <SelectFileToUploadCard
              key={card}
              {...{
                side: card,
                onError: (msg: string) => onError(msg, card),
                onFileChanged,
              }}
            />
            <Col margin="0 40px 24px 40px">
              {errors[card] && <StatusIndicator messageText={errors[card] || ""} status={Status.Negative} />}
            </Col>
          </>
        );
      })}
    </>
  );
};
